import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { eur, frDate, QUOTE_STATUS } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { FileText, Plus, Copy, Trash2, Mail, Loader2, Send, CheckCircle2, AlertCircle, Files } from "lucide-react";
import { toast } from "sonner";
import { Widget, PageHeader, KpiStrip } from "@/components/Widget";

export default function Quotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [sp, setSp] = useSearchParams();
  const [filter, setFilter] = useState(sp.get("filtre") === "impayees" ? "impayees" : "tous");
  const [delTarget, setDelTarget] = useState(null);
  const [sending, setSending] = useState(null);
  const remind = async (q, e) => {
    e.stopPropagation();
    setSending(q.id);
    try { await api.post(`/quotes/${q.id}/send-payment-reminder`); toast.success("Relance de paiement envoyée au client"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Envoi impossible"); }
    finally { setSending(null); }
  };

  useEffect(() => {
    if (sp.get("filtre") === "impayees") { setFilter("impayees"); }
  }, [sp]);
  const pickFilter = (k) => { setFilter(k); if (sp.get("filtre")) setSp({}, { replace: true }); };

  const load = () => api.get("/quotes").then(({ data }) => setQuotes(data));
  useEffect(() => { load(); }, []);

  const duplicate = async (id, e) => {
    e.stopPropagation();
    await api.post(`/quotes/${id}/duplicate`);
    toast.success("Devis dupliqué");
    load();
  };
  const remove = async () => {
    try { await api.delete(`/quotes/${delTarget.id}`); toast.success("Document supprimé"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Suppression impossible"); }
    finally { setDelTarget(null); }
  };

  const filters = [
    { k: "tous", l: "Tous" }, { k: "brouillon", l: "Brouillons" }, { k: "envoye", l: "Envoyés" },
    { k: "accepte", l: "Acceptés" }, { k: "facture", l: "Factures" }, { k: "impayees", l: "Factures impayées" }, { k: "refuse", l: "Refusés" },
  ];
  const isUnpaid = (q) => q.is_invoice && !(q.deposit_paid && q.balance_paid);
  const list = filter === "tous" ? quotes : filter === "impayees" ? quotes.filter(isUnpaid) : quotes.filter((q) => q.status === filter);
  const unpaid = quotes.filter(isUnpaid);
  const kpis = [
    { icon: Send, label: "Devis envoyés", value: quotes.filter((q) => q.status === "envoye").length, tint: "#FF6A00", onClick: () => pickFilter("envoye"), testid: "quotes-kpi-envoye", active: filter === "envoye" },
    { icon: CheckCircle2, label: "Devis acceptés", value: quotes.filter((q) => q.status === "accepte").length, tint: "#34d399", onClick: () => pickFilter("accepte"), testid: "quotes-kpi-accepte", active: filter === "accepte" },
    { icon: AlertCircle, label: "Factures impayées", value: unpaid.length, tint: "#f87171", onClick: () => pickFilter("impayees"), testid: "quotes-kpi-impayees", active: filter === "impayees" },
    { icon: FileText, label: "Montant impayé", value: eur(unpaid.reduce((a, q) => a + (q.total || 0), 0)), tint: "#FFD500", testid: "quotes-kpi-unpaid-amount" },
  ];

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <PageHeader title="Devis & Factures" subtitle={`${quotes.length} document(s).`} />
      <KpiStrip items={kpis} testid="quotes-kpis" />

      <Widget testid="quotes-widget" icon={Files} title={`Documents (${list.length})`} bodyClass="p-0"
        to="/app/calendrier" toLabel="Nouveau devis">
      <div className="flex gap-2 overflow-x-auto border-b border-zinc-800 px-4 py-3">
        {filters.map((f) => (
          <button key={f.k} data-testid={`filter-${f.k}`} onClick={() => pickFilter(f.k)}
            className={`shrink-0 whitespace-nowrap rounded-full border px-3 py-1 text-xs transition-colors ${filter === f.k ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"}`}>
            {f.l}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="py-16 text-center">
          <FileText className="mx-auto h-12 w-12 text-zinc-700" />
          <p className="mt-4 font-medium text-white">Aucun devis ici</p>
          <p className="mt-1 text-sm text-zinc-500">Un devis se crée depuis un événement.</p>
          <Button data-testid="quotes-empty-events" onClick={() => navigate("/app/calendrier")} className="mt-5 rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Créer depuis un événement</Button>
        </div>
      ) : (
        <div>
          {list.map((q) => (
            <div key={q.id} data-testid={`quote-row-${q.id}`} onClick={() => navigate(`/app/devis/${q.id}`)}
              className="flex cursor-pointer items-center gap-4 border-b border-zinc-800 p-4 last:border-0 hover:bg-zinc-800/40">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-zinc-800"><FileText className="h-5 w-5 text-[#FF6A00]" /></div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-white">{q.is_invoice ? q.invoice_number : q.number} · {q.title}</p>
                <p className="truncate text-sm text-zinc-500">{q.contact_name || "Sans contact"} · {frDate(q.created_at)}{q.last_payment_reminder_at && isUnpaid(q) ? <span data-testid={`quote-reminded-${q.id}`} className="text-[#FF6A00]"> · Relancé le {frDate(q.last_payment_reminder_at)}</span> : null}</p>
              </div>
              <span className="hidden font-display font-semibold text-white sm:block">{eur(q.total)}</span>
              <StatusBadge status={q.status} type="quote" />
              {isUnpaid(q) && (
                <button data-testid={`quote-remind-${q.id}`} onClick={(e) => remind(q, e)} disabled={sending === q.id}
                  className="flex items-center gap-1.5 rounded-full border border-[#FF6A00]/30 px-3 py-1.5 text-xs font-medium text-[#FF6A00] transition-colors hover:bg-[#FF6A00]/10 disabled:opacity-50" title="Envoyer une relance de paiement par email">
                  {sending === q.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}<span className="hidden sm:inline">Relancer</span>
                </button>
              )}
              <button data-testid={`quote-dup-${q.id}`} onClick={(e) => duplicate(q.id, e)} className="rounded-full p-2 text-zinc-500 hover:bg-zinc-800 hover:text-white" title="Dupliquer"><Copy className="h-4 w-4" /></button>
              <button data-testid={`quote-del-${q.id}`} onClick={(e) => { e.stopPropagation(); setDelTarget(q); }} className="rounded-full p-2 text-zinc-500 hover:bg-red-500/10 hover:text-red-400" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      </Widget>

      <ConfirmDialog
        open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}
        title="Supprimer ce document ?"
        description={delTarget ? `« ${(delTarget.is_invoice ? delTarget.invoice_number : delTarget.number) || ""} » sera définitivement supprimé et ne comptera plus dans vos statistiques.` : ""}
        onConfirm={remove} testid="del-quote-list-dialog"
      />
    </div>
  );
}
